import * as THREE from 'three';

/**
 * Canonical scene-rotation presets, shared by the Views buttons (CameraPanel)
 * and the keyboard shortcuts so the two can't drift apart.
 *
 * Named for what the CHAIR shows, not the sketch plane: the sketch is the
 * chair's side profile drawn on XY with the layers stacked in Z, so the
 * unrotated scene (0,0) looks straight at that profile - the SIDE elevation.
 * A quarter turn about Y looks across the layer stack - the FRONT elevation.
 * POSITIVE x tips the scene so the viewer looks DOWN at the chair.
 */
export const VIEW_PRESETS = Object.freeze({
    side: { x: 0, y: 0 },
    front: { x: 0, y: Math.PI / 2 },
    top: { x: Math.PI / 2, y: 0 },
    iso: { x: 0.6, y: -Math.PI / 4 }
});

/**
 * SceneRotationController - Manages scene rotation (not camera rotation)
 *
 * Matches Java's applyWorldTranslation approach where the scene is rotated
 * via GLOBAL.rotateModelsX and GLOBAL.rotateModelsY.
 *
 * Creates a pivot group that contains all scene objects, allowing rotation
 * around the world origin while keeping the camera fixed.
 */
export class SceneRotationController {
    constructor() {
        // Create pivot group for scene rotation
        this.pivotGroup = new THREE.Group();
        this.pivotGroup.name = 'SceneRotationPivot';

        // Rotation state (in radians)
        this.rotationX = 0; // Rotation around X axis (up/down tilt)
        this.rotationY = 0; // Rotation around Y axis (left/right spin)

        // Point (in pivot-group local space) the scene rotates around.
        // Updated to the chair's bounding-box centre so the model turns in
        // place instead of orbiting the world origin.
        this.pivotPoint = new THREE.Vector3(0, 0, 0);

        // Rotation speed
        this.rotationSpeed = 0.01; // Radians per key press

        // Auto-rotation state (matches Java's GLOBAL.autoRotate)
        this.autoRotate = false;
        this.autoRotateSpeed = -0.017; // Matches Java

        // Rotation constraints
        this.minRotationX = -Math.PI / 2; // -90 degrees
        this.maxRotationX = Math.PI / 2;  // +90 degrees
    }

    /**
     * Get the pivot group that should contain all scene objects
     * @returns {THREE.Group}
     */
    getPivotGroup() {
        return this.pivotGroup;
    }

    /**
     * Rotate scene around X axis (up/down tilt)
     * @param {number} delta - Rotation delta in radians
     */
    rotateX(delta) {
        this.rotationX += delta;

        // Clamp rotation to prevent flipping
        this.rotationX = Math.max(this.minRotationX, Math.min(this.maxRotationX, this.rotationX));

        this.updateRotation();
    }

    /**
     * Rotate scene around Y axis (left/right spin)
     * @param {number} delta - Rotation delta in radians
     */
    rotateY(delta) {
        this.rotationY += delta;
        this.updateRotation();
    }

    /**
     * Set absolute rotation
     * @param {number} x - X rotation in radians
     * @param {number} y - Y rotation in radians
     */
    setRotation(x, y) {
        this.rotationX = Math.max(this.minRotationX, Math.min(this.maxRotationX, x));
        this.rotationY = y;
        this.updateRotation();
    }

    /**
     * Get current rotation
     * @returns {{x: number, y: number}} Rotation in radians
     */
    getRotation() {
        return {
            x: this.rotationX,
            y: this.rotationY
        };
    }

    /**
     * Reset rotation to default view
     */
    reset() {
        this.rotationX = 0;
        this.rotationY = 0;
        this.updateRotation();
    }

    /**
     * Set the point the scene rotates around (in scene/local coordinates)
     * @param {THREE.Vector3} point - New pivot point
     */
    setPivotPoint(point) {
        this.pivotPoint.copy(point);
        this.updateRotation();
    }

    /**
     * Update the pivot group's rotation.
     *
     * A Group rotates around its own origin, so to rotate around an
     * arbitrary point C we add a translation that maps C back onto itself:
     *   position = C - R*C   =>   p' = R*p + C - R*C = C + R*(p - C)
     */
    updateRotation() {
        // Apply rotation in the same order as Java: X then Y
        this.pivotGroup.rotation.x = this.rotationX;
        this.pivotGroup.rotation.y = this.rotationY;

        const rotatedPivot = this.pivotPoint.clone().applyEuler(this.pivotGroup.rotation);
        this.pivotGroup.position.copy(this.pivotPoint).sub(rotatedPivot);
    }

    /**
     * Enable/disable auto-rotation (matches Java's GLOBAL.autoRotate)
     * @param {boolean} enabled
     */
    setAutoRotate(enabled) {
        this.autoRotate = enabled;
    }

    /**
     * Update auto-rotation (call in animation loop)
     * @param {number} deltaTime - Time since last frame (optional)
     */
    update(deltaTime = 1) {
        if (this.autoRotate) {
            this.rotateY(this.autoRotateSpeed * deltaTime);
        }
    }

    /**
     * Add object to rotatable scene group
     * @param {THREE.Object3D} object - Object to add
     */
    add(object) {
        this.pivotGroup.add(object);
    }

    /**
     * Remove object from rotatable scene group
     * @param {THREE.Object3D} object - Object to remove
     */
    remove(object) {
        this.pivotGroup.remove(object);
    }

    /**
     * Get rotation speed for keyboard controls
     * @returns {number}
     */
    getRotationSpeed() {
        return this.rotationSpeed;
    }

    /**
     * Set rotation speed
     * @param {number} speed - Radians per key press
     */
    setRotationSpeed(speed) {
        this.rotationSpeed = speed;
    }
}
